// components/carrito.js
import React, { useEffect, useState } from 'react';
import plantillaCarrito from './carrito.json';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';

const Carrito = () => {
  const navigate = useNavigate();
  const { 
    cart, 
    cartCount, 
    removeFromCart, 
    decreaseQuantity, 
    increaseQuantity, 
    clearCart
  } = useCart();

  const [elementorData, setElementorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  // 🔥 FUNCIÓN PARA FORMATEAR PRECIOS
  const formatPrice = (price) => {
    if (!price && price !== 0) return '0,00';
    
    if (typeof price === 'string' && price.includes(',')) {
      return price.replace('€', '').trim();
    }
    
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) return '0,00';
    
    return numericPrice.toFixed(2).replace('.', ',');
  };

  // 🔥 FUNCIÓN PARA CALCULAR EL RESUMEN DEL CARRITO
  const getCartSummary = () => {
    const subtotal = cart.reduce((total, item) => {
      const itemPrice = item.sale_price || item.price;
      return total + (itemPrice * item.quantity);
    }, 0);

    // Envío gratis para pedidos superiores a 50€
    const shipping = subtotal >= 50 ? 0 : 5.95;
    
    // Calcular IVA (21%)
    const tax = subtotal * 0.21;
    
    const finalTotal = subtotal + shipping + tax;

    return {
      subtotal,
      shipping,
      tax,
      finalTotal
    };
  };

  // 🔥 Cargar carrito desde localStorage
  useEffect(() => {
    const loadCart = () => {
      setElementorData(plantillaCarrito);
      setLoading(false);
    };

    loadCart();
  }, []);

  // 🔥 FUNCIÓN PARA PROCEDER AL PAGO
  const handleProceedToCheckout = () => {
    if (cart.length === 0) {
      alert('Tu carrito está vacío. Agrega productos antes de proceder al pago.');
      return;
    }
    
    // 🔥 NAVEGAR A LA PÁGINA DE CHECKOUT
    navigate('/checkout');
  };

  // 🔥 Renderizar el carrito personalizado (similar a WooCommerce)
  const renderCustomCart = () => {
    const { subtotal, shipping, tax, finalTotal } = getCartSummary();

    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        fontFamily: "'Montserrat', sans-serif"
      }}>
        {/* Header del carrito */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '2px solid #f0f0f0'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: '#333',
            margin: '0 0 10px 0'
          }}>
            🛒 Mi Carrito
          </h1>
          <p style={{
            color: '#666',
            fontSize: '16px',
            margin: 0
          }}>
            {cartCount} {cartCount === 1 ? 'producto' : 'productos'} en tu carrito
          </p>
        </div>

        {cart.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f8f9fa',
            borderRadius: '12px',
            marginBottom: '40px'
          }}>
            <div style={{ fontSize: '80px', marginBottom: '20px' }}>🛒</div>
            <h2 style={{ color: '#333', marginBottom: '10px' }}>Tu carrito está vacío</h2>
            <p style={{ color: '#666', marginBottom: '30px' }}>
              ¡Descubre nuestros productos y añade algunos a tu carrito!
            </p>
            <button 
              onClick={() => navigate('/tienda')}
              style={{
                background: '#1A3357',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'background 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#3496D2'}
              onMouseLeave={(e) => e.target.style.background = '#1A3357'}
            >
              Ir a la Tienda
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 400px',
            gap: '40px',
            alignItems: 'start'
          }}>
            {/* Lista de productos */}
            <div>
              {/* Header de la tabla */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr auto auto auto',
                gap: '20px',
                padding: '15px 20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '15px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                <div>Producto</div>
                <div>Descripción</div>
                <div style={{ textAlign: 'center' }}>Precio</div>
                <div style={{ textAlign: 'center' }}>Cantidad</div>
                <div style={{ textAlign: 'center' }}>Subtotal</div>
              </div>

              {/* Productos */}
              {cart.map(item => {
                const itemTotal = (item.sale_price || item.price) * item.quantity;
                
                return (
                  <div key={item.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 1fr auto auto auto',
                    gap: '20px',
                    padding: '20px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    alignItems: 'center',
                    background: 'white'
                  }}>
                    {/* Imagen */}
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    )}
                    
                    {/* Información del producto */}
                    <div>
                      <h3 style={{
                        margin: '0 0 5px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#333'
                      }}>
                        {item.name}
                      </h3>
                      
                      {/* 🔥 MOSTRAR ATRIBUTOS SI EXISTEN */}
                      {item.attributes && item.attributes.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          {item.attributes.map((attr, index) => (
                            <div key={index} style={{
                              fontSize: '12px',
                              color: '#666',
                              background: '#f5f5f5',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              display: 'inline-block',
                              marginRight: '8px',
                              marginBottom: '4px',
                              border: '1px solid #e0e0e0'
                            }}>
                              <strong style={{ color: '#333' }}>{attr.name}:</strong> {attr.option}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Categorías */}
                      {item.categories && item.categories.length > 0 && (
                        <div style={{ marginTop: '5px' }}>
                          {item.categories.slice(0, 2).map((cat, index) => (
                            <span key={index} style={{
                              background: '#e3f2fd',
                              color: '#1976d2',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              marginRight: '5px'
                            }}>
                              {cat.name || cat}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Precio unitario */}
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        color: item.sale_price ? '#e91e63' : '#2196f3',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}>
                        {formatPrice(item.sale_price || item.price)} €
                      </span>
                      {item.sale_price && (
                        <div style={{
                          color: '#999',
                          textDecoration: 'line-through',
                          fontSize: '12px'
                        }}>
                          {formatPrice(item.regular_price)} €
                        </div>
                      )}
                    </div>

                    {/* Controles de cantidad */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}>
                        <button 
                          onClick={() => decreaseQuantity(item.id)}
                          style={{
                            width: '35px',
                            height: '35px',
                            background: '#ff6b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#ff5252'}
                          onMouseLeave={(e) => e.target.style.background = '#ff6b6b'}
                        >
                          -
                        </button>
                        
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          minWidth: '40px',
                          textAlign: 'center',
                          padding: '8px',
                          background: '#f5f5f5',
                          borderRadius: '4px'
                        }}>
                          {item.quantity}
                        </span>
                        
                        <button 
                          onClick={() => increaseQuantity(item.id)}
                          style={{
                            width: '35px',
                            height: '35px',
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#45a049'}
                          onMouseLeave={(e) => e.target.style.background = '#4caf50'}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Total del producto */}
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        color: '#333',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}>
                        {formatPrice(itemTotal)} €
                      </span>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        style={{
                          marginTop: '10px',
                          background: 'transparent',
                          color: '#ff4444',
                          border: '1px solid #ff4444',
                          padding: '5px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'block',
                          width: '100%',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#ff4444';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = '#ff4444';
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Acciones del carrito */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '30px',
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <button 
                  onClick={() => navigate('/tienda')}
                  style={{
                    background: 'transparent',
                    color: '#1A3357',
                    border: '2px solid #1A3357',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#1A3357';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#1A3357';
                  }}
                >
                  ← Continuar comprando
                </button>
                
                <button 
                  onClick={clearCart}
                  style={{
                    background: 'transparent',
                    color: '#ff4444',
                    border: '2px solid #ff4444',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#ff4444';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#ff4444';
                  }}
                >
                  🗑️ Vaciar Carrito
                </button>
              </div>
            </div>

            {/* Resumen del pedido */}
            <div style={{
              background: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              position: 'sticky',
              top: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: '600',
                color: '#333',
                textAlign: 'center'
              }}>
                Resumen del Pedido
              </h3>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <span style={{ fontWeight: '500' }}>Subtotal:</span>
                  <span style={{ fontWeight: '600' }}>{formatPrice(subtotal)} €</span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <span style={{ fontWeight: '500' }}>Envío:</span>
                  <span style={{ 
                    fontWeight: '600',
                    color: shipping === 0 ? '#4caf50' : '#333'
                  }}>
                    {shipping === 0 ? 'GRATIS' : `${formatPrice(shipping)} €`}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <span style={{ fontWeight: '500' }}>IVA (21%):</span>
                  <span style={{ fontWeight: '600' }}>{formatPrice(tax)} €</span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  paddingTop: '15px',
                  borderTop: '2px solid #333'
                }}>
                  <span>Total:</span>
                  <span style={{ color: '#1A3357' }}>{formatPrice(finalTotal)} €</span>
                </div>

                {shipping > 0 && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    background: '#fff3cd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#856404',
                    textAlign: 'center',
                    border: '1px solid #ffeaa7'
                  }}>
                    🚚 Envío gratis en pedidos superiores a 50€
                  </div>
                )}
              </div>

              {/* 🔥 BOTÓN MODIFICADO: Ahora usa la nueva función handleProceedToCheckout */}
              <button 
                onClick={handleProceedToCheckout}
                style={{
                  width: '100%',
                  background: '#1A3357',
                  color: 'white',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '15px',
                  transition: 'background 0.3s ease',
                  boxShadow: '0 4px 8px rgba(26, 51, 87, 0.3)'
                }}
                onMouseEnter={(e) => e.target.style.background = '#3496D2'}
                onMouseLeave={(e) => e.target.style.background = '#1A3357'}
              >
                Proceder al Pago
              </button>

              <div style={{
                textAlign: 'center',
                fontSize: '12px',
                color: '#666',
                padding: '10px',
                background: '#e8f5e8',
                borderRadius: '4px',
                border: '1px solid #c8e6c9'
              }}>
                ✅ Pago seguro · 🔒 Datos protegidos
              </div>

              {/* Información adicional */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: '#e3f2fd',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#1976d2'
              }}>
                <strong>📦 Información de envío:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Envío en 24-48 horas</li>
                  <li>Devoluciones gratuitas en 30 días</li>
                  <li>Atención al cliente 24/7</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Estilos base similares a Tiendav3.js
  const styles = {
    container: {
      fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
      maxWidth: 'auto',
      margin: '0 auto'
    },
    loading: {
      textAlign: 'center',
      padding: '60px 20px'
    }
  };

  const parseStyles = (settings) => {
    if (!settings) return {};
    
    const elementorStyles = {};
    
    if (settings._margin) {
      elementorStyles.margin = `${settings._margin.top}% ${settings._margin.right}% ${settings._margin.bottom}% ${settings._margin.left}%`;
    }
    
    if (settings._padding) {
      elementorStyles.padding = `${settings._padding.top}% ${settings._padding.right}% ${settings._padding.bottom}% ${settings._margin.left}%`;
    }
    
    if (settings.background_color) {
      elementorStyles.backgroundColor = settings.background_color;
    }
    
    if (settings.align) {
      elementorStyles.textAlign = settings.align;
    }

    return elementorStyles;
  };

  const renderWidget = (widget, index) => {
    const { widgetType, settings = {}, id } = widget;
    const key = `${id || widgetType}-${index}`;

    const widgetRenderers = {
      'text-editor': () => (
        <div style={{...parseStyles(settings)}}>
          {/* Reemplazar el shortcode de WooCommerce con nuestro carrito personalizado */}
          {settings.editor && settings.editor.includes('[woocommerce_cart]') ? (
            renderCustomCart()
          ) : (
            <div dangerouslySetInnerHTML={{ __html: settings.editor }} />
          )}
        </div>
      ),
      'heading': () => (
        <div style={parseStyles(settings)}>
          <h1 style={{
            fontFamily: settings.typography_font_family || 'Montserrat',
            fontSize: settings.typography_font_size?.size + (settings.typography_font_size?.unit || 'px') || '35px',
            fontWeight: settings.typography_font_weight || '700',
            margin: 0,
            textAlign: settings.align || 'center',
            color: '#333'
          }}>
            {settings.title}
          </h1>
        </div>
      )
    };

    const renderer = widgetRenderers[widgetType];
    return renderer ? <div key={key}>{renderer()}</div> : null;
  };

  const renderElement = (element, index) => {
    if (!element) return null;

    const { elType, settings = {}, elements = [], id } = element;
    const key = `${id || elType}-${index}`;

    switch (elType) {
      case 'container':
        const containerStyle = {
          ...parseStyles(settings)
        };
        
        return (
          <div key={key} style={containerStyle}>
            {elements.map((child, idx) => renderElement(child, idx))}
          </div>
        );

      case 'widget':
        return renderWidget(element, index);

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔄</div>
        <h2>Cargando Carrito...</h2>
        <p>Diseño de Elementor + Carrito personalizado</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{background: '#ffe6e6', padding: '30px', borderRadius: '8px', textAlign: 'center', margin: '20px'}}>
        <h2>❌ Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <main>
        {elementorData && elementorData.content.map((element, index) => 
          renderElement(element, index)
        )}
      </main>
    </div>
  );
};

export default Carrito;